<template>
  <div class="search-results-container">
    <div class="section-inner">
      <!-- Search Header -->
      <div class="search-header" v-if="searchQuery">
        <div class="search-stats" v-if="!loading">
          <span v-if="collectionResults.length > 0">
            Found {{ collectionResults.length }} public {{ collectionResults.length === 1 ? 'course' : 'courses' }} for "<strong>{{ searchQuery }}</strong>"
          </span>
          <span v-else>No courses found for "<strong>{{ searchQuery }}</strong>"</span>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <i class="el-icon-loading"></i>
        <p>Searching...</p>
      </div>

      <!-- Results Grid (Plaza-style cards) -->
      <div class="content-grid" v-if="!loading && collectionResults.length > 0">
        <div
          v-for="item in collectionResults"
          :key="item.collection_id"
          class="content-card"
          @click="handleResultClick(item)"
        >
          <div class="card-cover">
            <CachedImage
              v-if="item.cover_url"
              :src="item.cover_url"
              :fallback-src="placeholderImage"
              fit="cover"
              width="100%"
              height="100%"
              lazy
            />
            <div v-else class="cover-placeholder">
              <i class="el-icon-reading"></i>
            </div>
          </div>
          <div class="card-body">
            <h4 class="card-title">{{ item.title }}</h4>
            <CollectionSpecialLabels :labels="item.special_labels" />
            <p class="card-desc">{{ item.description || 'No description' }}</p>
            <div class="card-footer">
              <div class="card-author">
                <el-avatar :size="20" :src="item.user_avatar" class="author-avatar">
                  {{ item.user_name ? item.user_name.charAt(0) : '?' }}
                </el-avatar>
                <span>{{ item.user_name || 'Unknown' }}</span>
              </div>
              <div class="card-tags" v-if="item.tags && item.tags.length > 0">
                <el-tag
                  v-for="tag in (item.tags || []).slice(0, 2)"
                  :key="tag"
                  size="mini"
                  effect="plain"
                  class="card-tag"
                >{{ tag }}</el-tag>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State (no query) -->
      <div v-if="!loading && !searchQuery" class="empty-state">
        <i class="el-icon-search"></i>
        <h3>Search for courses</h3>
        <p>Enter a search term to find courses</p>
      </div>

      <!-- Empty State (no results) -->
      <div v-if="!loading && searchQuery && collectionResults.length === 0" class="empty-state">
        <i class="el-icon-folder-opened"></i>
        <h3>No courses found</h3>
        <p>Try a different search term</p>
      </div>
    </div>
  </div>
</template>

<script>
import CollectionSpecialLabels from "@/components/CollectionSpecialLabels.vue";
import { searchPublicPosts } from '@/apis/front'
import { mapState } from 'vuex'
import CachedImage from '@/components/CachedImage.vue'
import { getCountryContextFromStore, isCountryScopeAvailable, withCountryContext } from '@/utils/countryAvailability'

export default {
  name: 'SearchResults',
  components: {
    CollectionSpecialLabels,
    CachedImage
  },
  data() {
    return {
      searchQuery: '',
      loading: false,
      collectionResults: [],
      placeholderImage: require('@/assets/Card Collection Cover Placeholder.jpeg')
    }
  },
  computed: {
    ...mapState(['user_id'])
  },
  created() {
    this.searchQuery = this.$route.query.q || this.$route.params.query || ''
    if (this.searchQuery) {
      this.performSearch()
    }
  },
  watch: {
    '$route'(to) {
      const newQuery = to.query.q || to.params.query || ''
      if (newQuery !== this.searchQuery) {
        this.searchQuery = newQuery
        if (this.searchQuery) {
          this.performSearch()
        }
      }
    }
  },
  methods: {
    async performSearch() {
      if (!this.searchQuery.trim()) return

      this.loading = true
      try {
        const response = await searchPublicPosts(withCountryContext({
          user_id: this.user_id,
          search_text: this.searchQuery
        }, this.$store))

        if (response.data && response.data.status === 'success') {
          const responseData = response.data.data || {}
          const baseCountryContext = getCountryContextFromStore(this.$store)
          const countryContext = {
            ...baseCountryContext,
            selected_country: responseData.selected_country || baseCountryContext.selected_country,
            ip_country: responseData.ip_country || baseCountryContext.ip_country
          }
          this.collectionResults = (responseData.collection_result || [])
            .map(item => isCountryScopeAvailable(item, countryContext)
              ? item
              : {
                  ...item,
                  status: item.status || 'PRIVATE',
                  settings: {
                    ...(item.settings || {}),
                    shown_in_public_search: false,
                    visible_in_recommendation: false
                  }
                })
            .filter(item =>
              isCountryScopeAvailable(item, countryContext) &&
              (item.status || 'PUBLISHED') === 'PUBLISHED' &&
              ((item.settings && item.settings.shown_in_public_search) !== false)
            )
        } else {
          this.$message.error('Search failed. Please try again.')
        }
      } catch (error) {
        console.error('Search error:', error)
        this.$message.error('Search failed. Please try again.')
      } finally {
        this.loading = false
      }
    },

    handleResultClick(item) {
      this.$store.dispatch('setTempCollectionId', item.collection_id)
      this.$router.push({ path: '/deck', query: { id: item.collection_id } })
    }
  }
}
</script>

<style scoped>
.search-results-container {
  width: 100%;
  min-height: 100vh;
  background: #f7f8fc;
}

.section-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ── Search Header ── */
.search-header {
  padding: 28px 0 20px;
}

.search-stats {
  font-size: 15px;
  color: #606266;
}

.search-stats strong {
  color: #303133;
}

/* ── Content Grid (matches Plaza) ── */
.content-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding-bottom: 60px;
}

.content-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.content-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}

.card-cover {
  width: 100%;
  aspect-ratio: 3 / 2;
  background: #eef1f6;
  overflow: hidden;
}

.cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8ecf1 0%, #d5dce6 100%);
}

.cover-placeholder i {
  font-size: 32px;
  color: #b0bac5;
}

.card-body {
  padding: 12px 14px 14px;
}

.card-title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.35;
}

.card-desc {
  margin: 0 0 10px;
  font-size: 12px;
  color: #909399;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.card-author {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #606266;
  min-width: 0;
}

.author-avatar {
  background: #e0e0e0;
  color: #606266;
  font-size: 10px;
  flex-shrink: 0;
}

.card-author span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-tags {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.card-tag {
  font-size: 11px;
  border-color: #e4e7ed;
  color: #909399;
}

/* ── Empty & Loading States ── */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 56px;
  color: #dcdfe6;
}

.empty-state h3 {
  margin: 16px 0 8px;
  color: #606266;
  font-weight: 600;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

.loading-state {
  text-align: center;
  padding: 80px 20px;
  color: #909399;
}

.loading-state i {
  font-size: 32px;
  color: #409eff;
}

.loading-state p {
  margin-top: 16px;
  font-size: 14px;
}

/* ── Responsive ── */
@media (max-width: 1100px) {
  .content-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .content-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .section-inner {
    padding: 0 16px;
  }
}

@media (max-width: 480px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}
</style>
